import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChefHat, Instagram, Facebook, Twitter, Mail, Phone, MapPin, Globe, Heart } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import type { DayHours } from '@/features/restaurants/types';

const LEGAL_API_KEYS = ['impressum', 'datenschutz', 'agb', 'widerruf', 'cookie', 'odr', 'alkohol'] as const;

interface PublicFooterProps {
  restaurantName: string;
  contactPhone?: string;
  contactEmail?: string;
  socialLinks?: Record<string, string>;
  openingHours?: DayHours[] | Record<string, Array<{ open: string; close: string }>>;
  address?: {
    street?: string;
    houseNumber?: string;
    postalCode?: string;
    city?: string;
    country?: string;
  };
  /** When provided, address link uses coordinates for accurate Google Maps pin */
  latitude?: number;
  longitude?: number;
  legalPages?: Record<string, string>;
  impressum?: string;
  datenschutz?: string;
  agb?: string;
  widerruf?: string;
  cookie?: string;
  odr?: string;
  alkohol?: string;
}

const DAY_KEYS: Record<string, string> = {
  SUNDAY: 'sunday', MONDAY: 'monday', TUESDAY: 'tuesday', WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday', FRIDAY: 'friday', SATURDAY: 'saturday',
};

const useFormatHoursForFooter = (hours: DayHours[] | undefined | Record<string, any>) => {
  const { t } = useTranslation();
  if (!hours) return [];

  let hoursArray: any[];
  if (Array.isArray(hours)) {
    hoursArray = hours;
  } else if (typeof hours === 'object' && hours !== null) {
    hoursArray = Object.entries(hours).map(([day, ranges]: [string, any]) => ({
      day: day.toUpperCase(),
      ranges: Array.isArray(ranges) ? ranges.map((r: any) => ({ openTime: r.openTime || r.open, closeTime: r.closeTime || r.close })) : [],
      isClosed: !ranges || (Array.isArray(ranges) && ranges.length === 0)
    }));
  } else {
    return [];
  }

  if (hoursArray.length === 0) return [];

  return hoursArray.map(h => {
    const dayKey = DAY_KEYS[h.day];
    const dayName = dayKey ? t(`days.${dayKey}`) : h.day;
    if (h.isClosed || !h.ranges || !Array.isArray(h.ranges) || h.ranges.length === 0) {
      return { day: dayName, time: t('public.closed') };
    }
    const timeStr = h.ranges.map((r: any) => {
      const openTime = r.openTime || r.open;
      const closeTime = r.closeTime || r.close;
      return `${openTime} - ${closeTime}`;
    }).join(', ');
    return { day: dayName, time: timeStr };
  });
};

const formatAddress = (address?: PublicFooterProps['address']): string => {
  if (!address) return '';
  const num = address.houseNumber?.trim();
  const streetBase = address.street?.trim() || '';
  const streetPart = num && streetBase && !streetBase.endsWith(num) ? `${streetBase} ${num}` : streetBase || num;
  const parts = [streetPart, address.postalCode, address.city, address.country].filter(Boolean);
  return parts.join(', ');
};
const formatAddressShort = (address?: PublicFooterProps['address']): string => {
  if (!address) return '';
  const num = address.houseNumber?.trim();
  const streetBase = address.street?.trim() || '';
  const streetPart = num && streetBase && !streetBase.endsWith(num) ? `${streetBase} ${num}` : streetBase || num;
  const parts = [streetPart, address.postalCode, address.city].filter(Boolean);
  return parts.join(', ');
};

const LEGAL_LABELS: Record<string, string> = {
  impressum: 'impressum', datenschutz: 'datenschutz', agb: 'agb', widerruf: 'widerrufsbelehrung',
  cookie: 'cookiePolicy', odr: 'odrNotice', alkohol: 'alcoholNotice',
  cookieBanner: 'cookiePolicy', widerrufsbelehrung: 'widerrufsbelehrung', odrNotice: 'odrNotice', alcoholNotice: 'alcoholNotice',
};

function getLegalLinks(props: PublicFooterProps): Array<{ key: string; value: string }> {
  const topLevel: Record<string, string | undefined> = {
    impressum: props.impressum, datenschutz: props.datenschutz, agb: props.agb, widerruf: props.widerruf,
    cookie: props.cookie, odr: props.odr, alkohol: props.alkohol,
  };
  const entries: Array<{ key: string; value: string }> = [];
  for (const key of LEGAL_API_KEYS) {
    const value = topLevel[key] ?? props.legalPages?.[key] ?? props.legalPages?.[key === 'widerruf' ? 'widerrufsbelehrung' : key === 'cookie' ? 'cookieBanner' : key === 'odr' ? 'odrNotice' : key === 'alkohol' ? 'alcoholNotice' : key];
    if (value?.trim()) entries.push({ key, value: value.trim() });
  }
  return entries;
}

const PublicFooter = (props: PublicFooterProps) => {
  const {
    restaurantName, contactPhone, contactEmail, socialLinks, openingHours,
    address, latitude, longitude, legalPages, impressum, datenschutz, agb, widerruf, cookie, odr, alkohol,
  } = props;
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId: string }>();
  const formattedHours = useFormatHoursForFooter(openingHours);
  const legalLinks = getLegalLinks(props);

  const getSocialIcon = (platform: string) => {
    const lower = platform.toLowerCase();
    if (lower.includes('instagram')) return Instagram;
    if (lower.includes('facebook')) return Facebook;
    if (lower.includes('twitter') || lower.includes('x')) return Twitter;
    return Globe;
  };

  return (
    <footer className="border-t border-border bg-card/50">
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-8 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-10">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center">
                <ChefHat className="w-5 h-5 text-accent" />
              </div>
              <span className="font-bold text-lg text-foreground">{restaurantName}</span>
            </div>
            {socialLinks && Object.keys(socialLinks).length > 0 && (
              <div className="flex gap-1.5 mb-4">
                {Object.entries(socialLinks).map(([platform, url]) => {
                  const Icon = getSocialIcon(platform);
                  return (
                    <Button
                      key={platform}
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 rounded-xl hover:bg-accent/10 hover:text-accent transition-colors"
                      asChild
                    >
                      <a href={url} target="_blank" rel="noopener noreferrer" aria-label={platform}>
                        <Icon className="w-4 h-4" />
                      </a>
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Contact */}
          <div className="min-w-0">
            <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">{t('public.contact')}</h4>
            <ul className="space-y-3.5 text-sm text-muted-foreground">
              {contactPhone && (
                <li className="flex items-center gap-2.5 min-w-0">
                  <Phone className="w-4 h-4 text-accent/70 shrink-0" />
                  <a href={`tel:${contactPhone}`} className="hover:text-foreground transition-colors break-all">{contactPhone}</a>
                </li>
              )}
              {contactEmail && (
                <li className="flex items-center gap-2.5 min-w-0">
                  <Mail className="w-4 h-4 text-accent/70 shrink-0" />
                  <a href={`mailto:${contactEmail}`} className="hover:text-foreground transition-colors break-all">{contactEmail}</a>
                </li>
              )}
              {address && (address.street || address.city) && (
                <li className="flex items-center gap-2.5 min-w-0 overflow-hidden">
                  <MapPin className="w-4 h-4 text-accent/70 shrink-0" aria-hidden />
                  <a
                    href={latitude != null && longitude != null
                      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([restaurantName, `${latitude},${longitude}`].filter(Boolean).join(', '))}`
                      : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([restaurantName, formatAddress(address)].filter(Boolean).join(', '))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-foreground transition-colors truncate min-w-0 block"
                    aria-label={t('public.openInGoogleMaps')}
                  >
                    <span className="sm:hidden">{formatAddressShort(address)}</span>
                    <span className="hidden sm:inline">{formatAddress(address)}</span>
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Hours */}
          {formattedHours.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">{t('public.openingHours')}</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {formattedHours.map((h, i) => (
                  <li key={i} className="flex justify-between gap-3">
                    <span className="font-medium text-foreground/70">{h.day}</span>
                    <span className="tabular-nums">{h.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Legal */}
          {legalLinks.length > 0 && (
            <div>
              <h4 className="font-semibold text-foreground mb-4 text-sm uppercase tracking-wider">{t('public.legal')}</h4>
              <ul className="space-y-2.5 text-sm">
                {legalLinks.map(({ key, value }) => {
                  const labelKey = LEGAL_LABELS[key];
                  const label = labelKey ? t(`public.${labelKey}`) : key;
                  const isUrl = value.startsWith('http://') || value.startsWith('https://');
                  return (
                    <li key={key}>
                      {isUrl ? (
                        <a href={value} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                          {label}
                        </a>
                      ) : (
                        <Link to={`/${restaurantId || ''}/legal/${key}`} className="text-muted-foreground hover:text-foreground transition-colors">
                          {label}
                        </Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <div className="pt-8 border-t border-border/60 flex flex-col sm:flex-row items-center justify-between gap-4 text-center sm:text-left">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} {restaurantName}. {t('public.allRightsReserved')}
          </p>
          <p className="text-sm text-muted-foreground flex items-center gap-1.5">
            {t('public.madeWith')} <Heart className="w-3 h-3 text-accent fill-accent" /> {t('public.by')}{' '}
            <a
              href="https://restroos.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-foreground hover:text-accent transition-colors"
            >
              restroos.com
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default PublicFooter;
